import React, { useState, useEffect } from "react";
import axios from "axios";
import config from "../config";
import { formatLastSeen } from "../utils/lastSeen";
import ImageViewer from "./ImageViewer";
import ProfileSharedContent from "./ProfileSharedContent";
import "../styles/UserProfile.css";

function UserProfile({
  user,
  selectedUserId,
  onClose,
  onBlock,
  onUnblock,
  socket,
}) {
  const [profileData, setProfileData] = useState(null);
  const [loading, setLoading] = useState(true); 
  const [error, setError] = useState("");
  const [showBlockConfirmation, setShowBlockConfirmation] = useState(false);
  const [showUnblockConfirmation, setShowUnblockConfirmation] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [imageViewer, setImageViewer] = useState({
    isOpen: false,
    imageUrl: "",
  });

  useEffect(() => {
    if (selectedUserId && user) {
      fetchUserProfile();
    }
  }, [selectedUserId, user]);

  const fetchUserProfile = async () => {
    try {
      setLoading(true);
      setError("");

      const response = await axios.get(
        `${config.apiUrl}/api/auth/profile/${selectedUserId}?requestingUserId=${user.id}`
      );

      setProfileData(response.data);
    } catch (error) {
      console.error("Error fetching user profile:", error);
      setError(error.response?.data?.error || "Failed to load user profile");
    } finally {
      setLoading(false);
    }
  };



  const handleBlock = async () => {
    try {
      setActionLoading(true);

      const response = await axios.post(
        `${config.apiUrl}/api/auth/block-user`,
        {
          userId: user.id,
          targetUserId: selectedUserId,
        }
      );

      // Update profile data to reflect blocked status
      setProfileData((prev) => ({
        ...prev,
        hasBlockedUser: true,
      }));

      setShowBlockConfirmation(false);

      // Emit WebSocket event for real-time updates
      if (socket) {
        socket.emit("userBlocked", {
          blockerId: user.id,
          blockedUserId: selectedUserId,
        });
      }

      // Notify parent component if callback provided
      if (onBlock) {
        onBlock(selectedUserId);
      }

      // Close the profile modal after blocking
      setTimeout(() => {
        onClose();
      }, 1000);
    } catch (error) {
      console.error("Error blocking user:", error);
      setError(error.response?.data?.error || "Failed to block user");
    } finally {
      setActionLoading(false);
    }
  };

  const handleUnblock = async () => {
    try {
      setActionLoading(true);

      const response = await axios.post(
        `${config.apiUrl}/api/auth/unblock-user`,
        {
          userId: user.id,
          targetUserId: selectedUserId,
        }
      );

      // Update profile data to reflect unblocked status
      setProfileData((prev) => ({
        ...prev,
        hasBlockedUser: false,
      }));

      setShowUnblockConfirmation(false);

      // Emit WebSocket event for real-time updates
      if (socket) {
        socket.emit("userUnblocked", {
          unblockerId: user.id,
          unblockedUserId: selectedUserId,
        });
      }

      // Notify parent component if callback provided
      if (onUnblock) {
        onUnblock(selectedUserId);
      }
        
      // Close the profile modal after unblocking
      setTimeout(() => {
        onClose();
      }, 1000);
    } catch (error) {
      console.error("Error unblocking user:", error);
      setError(error.response?.data?.error || "Failed to unblock user");
    } finally {
      setActionLoading(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const formatTimeAgo = (dateString) => {
    if (!dateString) return '';
    const now = new Date();
    const d = new Date(dateString);
    const diffMs = now - d;
    const mins = Math.floor(diffMs / 60000);
    const hours = Math.floor(diffMs / 3600000);
    const days = Math.floor(diffMs / 86400000);
    if (mins < 1) return 'Just now';
    if (mins < 60) return `${mins}m ago`;
    if (hours < 24) return `${hours}h ago`;
    if (days < 7) return `${days}d ago`;
    return d.toLocaleDateString();
  };



  if (!selectedUserId) {
    return null;
  }

  return (
    <div className="user-profile-overlay" onClick={onClose}>
      <div className="user-profile-modal" onClick={(e) => e.stopPropagation()}>
        <div className="profile-header">
          <button className="close-button" onClick={onClose}>
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
          <h2>Contact Info</h2>
        </div>

        {loading && (
          <div className="profile-loading">
            <div className="loading-spinner"></div>
            <p>Loading profile...</p>
          </div>
        )}

        {error && (
          <div className="profile-error">
            <p>{error}</p>
            <button onClick={fetchUserProfile} className="retry-button">
              Try Again
            </button>
          </div>
        )}

        {!loading && !error && profileData && (
          <div className="profile-content">
            {/* Profile Picture and Basic Info */}
            <div className="profile-picture-section">
              <div className="profile-picture-container">
                {profileData.profileImage ? (
                  <img
                    src={`${config.apiUrl}${profileData.profileImage}`}
                    alt={profileData.username}
                    className="profile-picture"
                    onClick={() => {
                      setImageViewer({
                        isOpen: true,
                        imageUrl: `${config.apiUrl}${profileData.profileImage}`,
                      });
                    }}
                    style={{ cursor: 'pointer' }}
                    title="Click to view full image"
                  />
                ) : (
                  <div className="profile-picture-placeholder notranslate">
                    {profileData.username.charAt(0).toUpperCase()}
                  </div>
                )}
              </div>
              <div className="profile-basic-info">
                <h3 className="profile-name notranslate">
                  {profileData.username}
                </h3>
                <p className="profile-status">
                  {formatLastSeen(
                    profileData.lastSeen,
                    profileData.lastSeen != null,
                    "profile"
                  )}
                </p>
              </div>
            </div>

            {/* Bio Section */}
            <div className="profile-section">
              <h4 className="section-title">About</h4>
              <p className="profile-bio">
                {profileData.bio !== null
                  ? profileData.bio || "No bio available"
                  : "Bio is hidden"}
              </p>
            </div>

            {/* Member Since */}
            <div className="profile-section">
              <h4 className="section-title">Member Since</h4>
              <p className="profile-joined">
                {formatDate(profileData.joinedAt)}
              </p>
            </div>

            {/* Shared media, links, docs (WhatsApp-style) */}
            <ProfileSharedContent
              user={user}
              contactUserId={selectedUserId}
            />

            {/* Action Buttons */}
            <div className="profile-actions">
              {profileData.hasBlockedUser ? (
                <button
                  className="action-button unblock-button"
                  onClick={() => setShowUnblockConfirmation(true)}
                  disabled={actionLoading}
                >
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <rect x="3" y="11" width="18" height="10" rx="2" ry="2" />
                    <circle cx="12" cy="16" r="1" />
                    <path d="M7 11V7a5 5 0 0 1 9.9-1" />
                  </svg>
                  Unblock Contact
                </button>
              ) : (
                <button
                  className="action-button block-button"
                  onClick={() => setShowBlockConfirmation(true)}
                  disabled={actionLoading}
                >
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                    <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                  </svg>
                  Block Contact
                </button>
              )}
            </div>

            {/* Block Status Warning */}
            {profileData.isBlockedByUser && (
              <div className="block-status-warning">
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
                  <line x1="12" y1="9" x2="12" y2="13" />
                  <line x1="12" y1="17" x2="12.01" y2="17" />
                </svg>
                <p>You have been blocked by this user</p>
              </div>
            )}
          </div>
        )}

        {/* Block Confirmation Modal */}
        {showBlockConfirmation && (
          <div className="confirmation-overlay" onClick={() => setShowBlockConfirmation(false)}>
            <div className="confirmation-modal" onClick={(e) => e.stopPropagation()}>
              <h3>Block Contact</h3>
              <p>
                Are you sure you want to block{" "}
                <strong>{profileData?.username}</strong>?
                <br />
                <br />
                Blocked contacts will no longer be able to send you messages.
              </p>
              <div className="confirmation-actions">
                <button
                  className="cancel-button"
                  onClick={() => setShowBlockConfirmation(false)}
                  disabled={actionLoading}
                >
                  Cancel
                </button>
                <button
                  className="confirm-button block-confirm"
                  onClick={handleBlock}
                  disabled={actionLoading}
                >
                  {actionLoading ? "Blocking..." : "Yes, Block"}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Unblock Confirmation Modal */}
        {showUnblockConfirmation && (
          <div className="confirmation-overlay" onClick={() => setShowUnblockConfirmation(false)}>
            <div className="confirmation-modal" onClick={(e) => e.stopPropagation()}>
              <h3>Unblock Contact</h3>
              <p>
                Are you sure you want to unblock{" "}
                <strong>{profileData?.username}</strong>?
                <br />
                <br />
                This contact will be able to send you messages again.
              </p>
              <div className="confirmation-actions">
                <button
                  className="cancel-button"
                  onClick={() => setShowUnblockConfirmation(false)}
                  disabled={actionLoading}
                >
                  Cancel
                </button>
                <button
                  className="confirm-button unblock-confirm"
                  onClick={handleUnblock}
                  disabled={actionLoading}
                >
                  {actionLoading ? "Unblocking..." : "Yes, Unblock"}
                </button>
              </div>
            </div>
          </div>
        )}



        {/* Image Viewer Modal */}
        <ImageViewer
          isOpen={imageViewer.isOpen}
          imageUrl={imageViewer.imageUrl}
          onClose={() => setImageViewer({ isOpen: false, imageUrl: "" })}
        />
      </div>
    </div>
  );
}

export default UserProfile;
